import { db, storage } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  arrayRemove
} from 'firebase/firestore';
import { ref, listAll, deleteObject, getDownloadURL } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import logger from '../utils/logger';

/**
 * Service for handling account deletion, data cleanup, and related operations
 */
class AccountDeletionService {
  /**
   * Delete all user data from Firestore (but keep auth account)
   * Used for Smart Join Flow "delete and continue" option
   * @param {string} userId - User ID
   * @param {string} familyCode - User's current family code (if any)
   * @returns {Promise<void>}
   */
  async deleteUserData(userId, familyCode = null) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const batch = writeBatch(db);

    // 1. Delete from nested family member collection
    if (familyCode) {
      const familyRef = doc(db, 'families', familyCode);
      const memberRef = doc(familyRef, 'members', userId);
      const memberDoc = await getDoc(memberRef);

      if (memberDoc.exists()) {
        batch.delete(memberRef);

        // Remove from group members arrays
        const groupsCollection = collection(familyRef, 'groups');
        const groupsSnapshot = await getDocs(groupsCollection);

        for (const groupDoc of groupsSnapshot.docs) {
          const groupData = groupDoc.data();
          if (groupData.members && groupData.members.includes(userId)) {
            batch.update(groupDoc.ref, {
              members: arrayRemove(userId),
              updatedAt: new Date()
            });
          }
        }

        // Update family member count
        const familyDoc = await getDoc(familyRef);
        if (familyDoc.exists()) {
          const currentCount = familyDoc.data().memberCount || 1;
          batch.update(familyRef, {
            memberCount: Math.max(0, currentCount - 1),
            updatedAt: new Date()
          });
        }
      }
    }

    // 2. Delete top-level user document
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      // If familyCode wasn't provided, try to get it from user doc
      const userData = userDoc.data();
      if (!familyCode && userData.familyCode) {
        // Recursively handle family cleanup
        await this.deleteUserData(userId, userData.familyCode);
        return;
      }
      batch.delete(userRef);
    }

    // 3. Delete user's invitations (ones they sent)
    if (familyCode) {
      const invitationsRef = collection(db, 'families', familyCode, 'invitations');
      const invitationsQuery = query(invitationsRef, where('inviterId', '==', userId));
      const invitationsSnapshot = await getDocs(invitationsQuery);

      for (const invDoc of invitationsSnapshot.docs) {
        batch.delete(invDoc.ref);
      }
    }

    // 4. Delete user's access requests
    const accessRequestsRef = collection(db, 'accessRequests');
    const requestsQuery = query(accessRequestsRef, where('requesterId', '==', userId));
    const requestsSnapshot = await getDocs(requestsQuery);

    for (const reqDoc of requestsSnapshot.docs) {
      batch.delete(reqDoc.ref);
    }

    await batch.commit();

    // 5. Delete user's files from storage (async, non-blocking)
    this.deleteUserStorage(userId).catch(err => {
      logger.error('Error deleting user storage:', err);
      // Don't throw - storage deletion is best-effort
    });
  }

  /**
   * Delete user's files from Firebase Storage
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async deleteUserStorage(userId) {
    try {
      const userStorageRef = ref(storage, `users/${userId}`);
      const listResult = await listAll(userStorageRef);

      // Delete all files
      const deletePromises = listResult.items.map(item => deleteObject(item));

      // Recursively delete folders
      for (const folderRef of listResult.prefixes) {
        const folderResult = await listAll(folderRef);
        const folderDeletePromises = folderResult.items.map(item => deleteObject(item));
        deletePromises.push(...folderDeletePromises);
      }

      await Promise.all(deletePromises);
    } catch (err) {
      // Storage path might not exist, which is fine
      if (err.code !== 'storage/object-not-found') {
        throw err;
      }
    }
  }

  /**
   * Schedule account deletion with grace period
   * Creates a deletion request that will be processed after the grace period
   * @param {string} userId - User ID
   * @param {Object} options - Deletion options
   * @param {number} options.gracePeriodDays - Days before permanent deletion (default: 30)
   * @param {string} options.reason - Optional reason for deletion
   * @returns {Promise<Object>} Deletion request details
   */
  async scheduleAccountDeletion(userId, options = {}) {
    const { gracePeriodDays = 30, reason = '' } = options;

    // Get user data first
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();

    // Create deletion request
    const deletionRef = doc(db, 'accountDeletionRequests', userId);
    const deletionRequest = {
      userId,
      email: userData.email,
      displayName: userData.displayName,
      familyCode: userData.familyCode || null,
      reason,
      status: 'pending', // pending, cancelled, completed
      requestedAt: new Date(),
      scheduledDeletionDate: new Date(Date.now() + gracePeriodDays * 24 * 60 * 60 * 1000),
      gracePeriodDays,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(deletionRef, deletionRequest);

    // Update user document to mark as pending deletion
    await updateDoc(userRef, {
      accountStatus: 'pending_deletion',
      deletionScheduledAt: deletionRequest.scheduledDeletionDate,
      updatedAt: new Date()
    });

    // Notify family admins if user is in a family
    if (userData.familyCode) {
      await this.notifyAdminsOfPendingDeletion(userId, userData.familyCode, deletionRequest);
    }

    return deletionRequest;
  }

  /**
   * Cancel a scheduled account deletion
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async cancelAccountDeletion(userId) {
    const deletionRef = doc(db, 'accountDeletionRequests', userId);
    const deletionDoc = await getDoc(deletionRef);

    if (!deletionDoc.exists()) {
      throw new Error('No pending deletion request found');
    }

    const deletionData = deletionDoc.data();
    if (deletionData.status !== 'pending') {
      throw new Error('Deletion request is not in pending status');
    }

    // Update deletion request status
    await updateDoc(deletionRef, {
      status: 'cancelled',
      cancelledAt: new Date(),
      updatedAt: new Date()
    });

    // Update user document
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      accountStatus: 'active',
      deletionScheduledAt: null,
      updatedAt: new Date()
    });
  }

  /**
   * Immediately delete an account (no grace period)
   * @param {string} userId - User ID
   * @param {Object} authUser - Firebase auth user object
   * @returns {Promise<void>}
   */
  async deleteAccountImmediately(userId, authUser) {
    // Get user data before deletion
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();

      // Notify family admins before deletion
      if (userData.familyCode) {
        await this.notifyAdminsOfDeletion(userId, userData.familyCode, userData);
      }

      // Delete all user data
      await this.deleteUserData(userId, userData.familyCode);
    }

    // Delete any pending deletion requests
    const deletionRef = doc(db, 'accountDeletionRequests', userId);
    const deletionDoc = await getDoc(deletionRef);
    if (deletionDoc.exists()) {
      await deleteDoc(deletionRef);
    }

    // Delete the Firebase Auth account
    if (authUser) {
      await deleteUser(authUser);
    }
  }

  /**
   * Export user data before deletion
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User's exportable data
   */
  async exportUserData(userId) {
    const exportData = {
      exportedAt: new Date().toISOString(),
      userId,
      profile: null,
      familyMembership: null,
      relationships: [],
      invitationsSent: [],
      photos: [],
      documents: []
    };

    // Get user profile
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      exportData.profile = {
        email: userData.email,
        displayName: userData.displayName,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        bio: userData.bio,
        birthday: userData.birthday,
        createdAt: userData.createdAt,
        professionalProfile: userData.professionalProfile,
        businessInfo: userData.businessInfo
      };

      // Get family membership data
      if (userData.familyCode) {
        const familyRef = doc(db, 'families', userData.familyCode);
        const memberRef = doc(familyRef, 'members', userId);
        const memberDoc = await getDoc(memberRef);

        if (memberDoc.exists()) {
          const memberData = memberDoc.data();
          exportData.familyMembership = {
            familyCode: userData.familyCode,
            role: memberData.role,
            groups: memberData.groups,
            relationship: memberData.relationship,
            joinedAt: memberData.joinedAt
          };
        }

        // Get relationships
        if (userData.relationships) {
          exportData.relationships = Object.entries(userData.relationships).map(([relId, rel]) => ({
            relatedUserId: relId,
            type: rel.type,
            name: rel.name,
            addedAt: rel.addedAt
          }));
        }

        // Get invitations sent
        const invitationsRef = collection(familyRef, 'invitations');
        const invitationsQuery = query(invitationsRef, where('inviterId', '==', userId));
        const invitationsSnapshot = await getDocs(invitationsQuery);

        exportData.invitationsSent = invitationsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            inviteCode: data.inviteCode,
            inviteeEmail: data.inviteeEmail,
            relationship: data.relationship,
            status: data.status,
            sentAt: data.sentAt
          };
        });
      }

      // Get photo URLs (metadata only, not actual files)
      try {
        const userStorageRef = ref(storage, `users/${userId}/photos`);
        const photoList = await listAll(userStorageRef);
        exportData.photos = await Promise.all(
          photoList.items.map(async (item) => {
            try {
              const url = await getDownloadURL(item);
              return { name: item.name, url };
            } catch {
              return { name: item.name, url: null };
            }
          })
        );
      } catch {
        // Photos folder might not exist
      }
    }

    return exportData;
  }

  /**
   * Notify family admins of pending deletion
   * @private
   */
  async notifyAdminsOfPendingDeletion(userId, familyCode, deletionRequest) {
    try {
      const familyRef = doc(db, 'families', familyCode);
      const membersRef = collection(familyRef, 'members');
      const membersSnapshot = await getDocs(membersRef);

      const adminNotifications = [];

      for (const memberDoc of membersSnapshot.docs) {
        const memberData = memberDoc.data();
        if (memberData.groups?.includes('ADMIN') && memberDoc.id !== userId) {
          // Create notification for admin
          const notificationRef = doc(collection(db, 'notifications'));
          adminNotifications.push(
            setDoc(notificationRef, {
              recipientId: memberDoc.id,
              type: 'member_pending_deletion',
              title: 'Member Scheduled for Deletion',
              message: `${deletionRequest.displayName || deletionRequest.email} has scheduled their account for deletion. Their account will be deleted on ${deletionRequest.scheduledDeletionDate.toLocaleDateString()}.`,
              relatedUserId: userId,
              familyCode,
              read: false,
              createdAt: new Date()
            })
          );
        }
      }

      await Promise.all(adminNotifications);
    } catch (err) {
      logger.error('Error notifying admins:', err);
      // Don't throw - notifications are best-effort
    }
  }

  /**
   * Notify family admins of deletion completion
   * @private
   */
  async notifyAdminsOfDeletion(userId, familyCode, userData) {
    try {
      const familyRef = doc(db, 'families', familyCode);
      const membersRef = collection(familyRef, 'members');
      const membersSnapshot = await getDocs(membersRef);

      const adminNotifications = [];

      for (const memberDoc of membersSnapshot.docs) {
        const memberData = memberDoc.data();
        if (memberData.groups?.includes('ADMIN') && memberDoc.id !== userId) {
          const notificationRef = doc(collection(db, 'notifications'));
          adminNotifications.push(
            setDoc(notificationRef, {
              recipientId: memberDoc.id,
              type: 'member_deleted',
              title: 'Member Account Deleted',
              message: `${userData.displayName || userData.email} has deleted their account and left the family.`,
              relatedUserId: userId,
              familyCode,
              read: false,
              createdAt: new Date()
            })
          );
        }
      }

      await Promise.all(adminNotifications);
    } catch (err) {
      logger.error('Error notifying admins:', err);
    }
  }

  /**
   * Get pending deletion status for a user
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} Deletion request or null
   */
  async getDeletionStatus(userId) {
    const deletionRef = doc(db, 'accountDeletionRequests', userId);
    const deletionDoc = await getDoc(deletionRef);

    if (!deletionDoc.exists()) {
      return null;
    }

    return {
      id: deletionDoc.id,
      ...deletionDoc.data()
    };
  }

  /**
   * Process expired deletion requests (called by Cloud Function or admin)
   * @returns {Promise<number>} Number of accounts deleted
   */
  async processExpiredDeletions() {
    const now = new Date();
    const deletionsRef = collection(db, 'accountDeletionRequests');
    const expiredQuery = query(
      deletionsRef,
      where('status', '==', 'pending'),
      where('scheduledDeletionDate', '<=', now)
    );

    const expiredDocs = await getDocs(expiredQuery);
    let deletedCount = 0;

    for (const deletionDoc of expiredDocs.docs) {
      try {
        const deletionData = deletionDoc.data();
        await this.deleteUserData(deletionData.userId, deletionData.familyCode);

        await updateDoc(deletionDoc.ref, {
          status: 'completed',
          completedAt: new Date(),
          updatedAt: new Date()
        });

        deletedCount++;
      } catch (err) {
        logger.error(`Error processing deletion for ${deletionDoc.id}:`, err);
      }
    }

    return deletedCount;
  }
}

export const accountDeletionService = new AccountDeletionService();
